/* PathwayCoverageCard — показва колко от active phenotype pathway-и е покрито
 * от днешното меню (или last-added meals).
 *
 * Reads: GET /api/pathway-score/my-phenotype  → проверява имa ли research
 *        GET /api/pathway-score/daily?date    → actual coverage
 *
 * Визуализация:
 *   - Header: "Pathway Coverage · TNBC" + overall score (6/7 интервенции · 3/4 pathway-и)
 *   - Hits list (зелени badges с evidence level)
 *   - Misses list (сиви badges)
 *   - Active KEGG pathways — clickable към kegg.jp
 *
 * Не се рендерира, ако user-ът няма assigned phenotype с research record.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Target, CheckCircle2, Circle, ExternalLink, TrendingUp, Lightbulb, Plus, User } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const EVIDENCE_COLORS = {
  A: { bg: "bg-emerald-500/20", text: "text-emerald-400 dark:text-emerald-300", light: "bg-emerald-100 text-emerald-700" },
  B: { bg: "bg-amber-500/20", text: "text-amber-400 dark:text-amber-300", light: "bg-amber-100 text-amber-700" },
  C: { bg: "bg-gray-500/20", text: "text-gray-400", light: "bg-gray-100 text-gray-600" },
};

const MENO_LABEL = {
  premenopausal:  { bg: "Пременопауза",  en: "Premenopausal",  es: "Premenopáusica" },
  perimenopausal: { bg: "Перименопауза", en: "Perimenopausal", es: "Perimenopáusica" },
  postmenopausal: { bg: "Постменопауза", en: "Postmenopausal", es: "Postmenopáusica" },
};

// Локализиран тригер за meno-toggle/фрази в card-а — централизирано за #19.
const T = {
  bg: {
    pathwayCoverage: "Покритие на пътищата",
    interventions: "интервенции",
    pathways: "pътища",
    mealsToday: "хранения днес",
    stratified: "stratified",
    coveredInterventions: "Покрити интервенции",
    uncovered: "Непокрити",
    uncoveredHint: "клик на 💡 за препоръки",
    activatedKegg: "Активирани KEGG пътища",
    closeGap: "Затвори",
    hideGap: "Скрий",
    suggestFood: "Предложи храна за затваряне на gap",
    cleared: "Изчистено",
    error: "Грешка",
    setMeno: (lbl) => `Зададено: ${lbl}`,
    menoStratified: "Menopausal-stratified scoring",
    calculating: "Изчислявам pathway coverage...",
  },
  en: {
    pathwayCoverage: "Pathway Coverage",
    interventions: "interventions",
    pathways: "pathways",
    mealsToday: "meals today",
    stratified: "stratified",
    coveredInterventions: "Covered interventions",
    uncovered: "Uncovered",
    uncoveredHint: "click 💡 for suggestions",
    activatedKegg: "Activated KEGG pathways",
    closeGap: "Close",
    hideGap: "Hide",
    suggestFood: "Suggest food to close gap",
    cleared: "Cleared",
    error: "Error",
    setMeno: (lbl) => `Set: ${lbl}`,
    menoStratified: "Menopausal-stratified scoring",
    calculating: "Computing pathway coverage...",
  },
  es: {
    pathwayCoverage: "Cobertura de rutas",
    interventions: "intervenciones",
    pathways: "rutas",
    mealsToday: "comidas hoy",
    stratified: "estratificado",
    coveredInterventions: "Intervenciones cubiertas",
    uncovered: "No cubiertas",
    uncoveredHint: "haga clic en 💡 para sugerencias",
    activatedKegg: "Rutas KEGG activadas",
    closeGap: "Cerrar",
    hideGap: "Ocultar",
    suggestFood: "Sugerir comida para cerrar la brecha",
    cleared: "Limpiado",
    error: "Error",
    setMeno: (lbl) => `Establecido: ${lbl}`,
    menoStratified: "Puntuación estratificada por menopausia",
    calculating: "Calculando cobertura de rutas...",
  },
};

function useTr() {
  const { language } = useLanguage();
  const lang = language === "es" ? "es" : language === "en" ? "en" : "bg";
  return { lang, t: T[lang], menoLabel: (k) => MENO_LABEL[k]?.[lang] || k };
}

// ───────── Menopausal status toggle — inline under header ─────────
function MenoToggle({ API, current, onChanged }) {
  const { isDark } = useTheme();
  const { lang, t, menoLabel } = useTr();
  const [saving, setSaving] = useState(false);

  const set = async (status) => {
    setSaving(true);
    try {
      await axios.post(`${API}/pathway-score/menopausal-status`, { status: status || "" });
      toast.success(status ? t.setMeno(menoLabel(status)) : t.cleared);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-md p-2 text-[10px] ${isDark ? "bg-white/5" : "bg-white/60"}`}
      data-testid="meno-toggle">
      <div className={`flex items-center gap-1 mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        <User className="w-3 h-3" />
        <span className="uppercase tracking-wider font-semibold">{t.menoStratified}</span>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {["premenopausal", "perimenopausal", "postmenopausal"].map((k) => {
          const active = current === k;
          return (
            <button key={k} onClick={() => set(active ? "" : k)} disabled={saving}
              className={`px-2 py-1 rounded-full text-[10px] font-semibold transition-colors disabled:opacity-50
                ${active
                  ? "bg-pink-500 text-white"
                  : (isDark ? "bg-white/5 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:text-gray-800")}`}
              data-testid={`meno-btn-${k}`}>
              {menoLabel(k)}
            </button>
          );
        })}
      </div>
      {current && (
        <div className={`mt-1 text-[9px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {(() => {
            const tips = {
              bg: {
                peri: "Перимено: екстра тежест на фибри + фитоестрогени за симптомна подкрепа (hot flashes, настроение).",
                clear: "Натиснете активния бутон, за да изчистите.",
              },
              en: {
                peri: "Perimeno: extra weight on fiber + phytoestrogens for symptom support (hot flashes, mood).",
                clear: "Click the active button to clear.",
              },
              es: {
                peri: "Perimeno: peso extra en fibra + fitoestrógenos para apoyo de síntomas (sofocos, ánimo).",
                clear: "Haga clic en el botón activo para limpiar.",
              },
            };
            const phrases = tips[lang] || tips.bg;
            return current === "perimenopausal" ? phrases.peri : phrases.clear;
          })()}
        </div>
      )}
    </div>
  );
}

// ───────── Gap Suggest Panel — inline под всеки missed intervention ─────────
function GapSuggestPanel({ API, interventionId, date, onAdded }) {
  const { isDark } = useTheme();
  const { lang } = useTr();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // holds food name currently being added

  const G = {
    bg: { computing: "Изчислявам...", noRecs: "Няма конкретни препоръки в базата.",
          ifAdd: "Ако добавите:", added: (n) => `Добавено към междинно: ${n}`,
          addError: "Грешка при добавяне", addBtn: "Добави", addTitle: "Добави като междинно хранене днес" },
    en: { computing: "Computing...", noRecs: "No specific recommendations available.",
          ifAdd: "If you add:", added: (n) => `Added as snack: ${n}`,
          addError: "Add failed", addBtn: "Add", addTitle: "Add as snack today" },
    es: { computing: "Calculando...", noRecs: "No hay recomendaciones específicas.",
          ifAdd: "Si añade:", added: (n) => `Añadido como tentempié: ${n}`,
          addError: "Error al añadir", addBtn: "Añadir", addTitle: "Añadir como tentempié hoy" },
  };
  const g = G[lang] || G.bg;

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data: resp } = await axios.post(`${API}/pathway-score/suggest-gap`, {
          intervention_id: interventionId,
          date,
        });
        if (!cancel) setData(resp);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, interventionId, date]);

  const addFood = async (food) => {
    setAdding(food.name_bg);
    try {
      const today = date || new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/meals/add`, {
        food_name: food.name_bg,
        amount_g: food.portion_g,
        calories: food.calories,
        protein: 0, carbs: 0, fat: 0,
        meal_type: "snack2",
        date: today,
        source: "pathway-gap-suggestion",
      });
      toast.success(g.added(food.name_bg));
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || g.addError);
    } finally {
      setAdding(null);
    }
  };

  if (loading) {
    return <div className={`text-[10px] italic px-2 py-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>{g.computing}</div>;
  }
  if (!data?.suggestions?.length) {
    return <div className={`text-[10px] italic px-2 py-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>{g.noRecs}</div>;
  }

  const cur = data.current;
  const proj = data.projected;
  const deltaPct = proj && cur ? (proj.coverage_pct - cur.coverage_pct) : 0;
  const newPathways = proj && cur ? (proj.pathway_coverage_count - cur.pathway_coverage_count) : 0;

  return (
    <div className={`mt-1 rounded-md p-2 border-l-2 space-y-2 ${isDark ? "border-amber-400/50 bg-amber-500/5" : "border-amber-400 bg-amber-50/60"}`}
      data-testid={`gap-suggest-panel-${interventionId}`}>
      {/* Projected delta */}
      {proj && (
        <div className={`text-[10px] leading-snug ${isDark ? "text-amber-200" : "text-amber-900"}`}>
          <span className="font-semibold">{g.ifAdd}</span>{" "}
          coverage {cur.coverage_pct}% → <span className="font-bold">{proj.coverage_pct}%</span>
          {deltaPct > 0 && <span className="text-emerald-500 font-semibold"> (+{deltaPct.toFixed(1)}%)</span>}
          {newPathways > 0 && <span>{" · "}+{newPathways} pathway{newPathways > 1 ? "-и" : ""}</span>}
        </div>
      )}

      {/* Mechanism reminder */}
      {data.mechanism_bg && (
        <div className={`text-[10px] italic ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          💡 {data.mechanism_bg.slice(0, 140)}{data.mechanism_bg.length > 140 ? "…" : ""}
        </div>
      )}

      {/* Food suggestion chips */}
      <div className="space-y-1">
        {data.suggestions.map((food) => (
          <div key={food.name_bg}
            className={`rounded-md p-1.5 flex items-start gap-2 ${isDark ? "bg-black/20" : "bg-white"}`}
            data-testid={`gap-food-${food.name_en || food.name_bg}`}>
            <div className="flex-1 min-w-0">
              <div className={`text-[11px] font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                {food.name_bg}
                <span className={`ml-1 text-[9px] font-mono ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {food.portion_g}g · {food.calories} kcal
                </span>
              </div>
              <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {food.rationale}
              </div>
            </div>
            <button onClick={() => addFood(food)} disabled={adding === food.name_bg}
              className={`shrink-0 inline-flex items-center gap-0.5 px-2 py-1 rounded text-[10px] font-semibold transition-colors disabled:opacity-50
                ${isDark ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
              data-testid={`add-food-btn-${food.name_en || food.name_bg}`}
              title={g.addTitle}>
              {adding === food.name_bg ? "…" : (<><Plus className="w-2.5 h-2.5" /> {g.addBtn}</>)}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PathwayCoverageCard({ refreshKey = 0, date = null }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { t, menoLabel } = useTr();
  const [phenotype, setPhenotype] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openGap, setOpenGap] = useState(null); // intervention_id of currently open suggestion
  const [localRefresh, setLocalRefresh] = useState(0);

  const fetchPhenotype = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pathway-score/my-phenotype`);
      setPhenotype(data);
      return data?.has_research;
    } catch {
      setPhenotype(null);
      return false;
    }
  }, [API]);

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const params = date ? `?date=${date}` : "";
      const { data } = await axios.get(`${API}/pathway-score/daily${params}`);
      setCoverage(data);
    } catch {
      setCoverage(null);
    } finally {
      setLoading(false);
    }
  }, [API, date]);

  useEffect(() => {
    (async () => {
      const has = await fetchPhenotype();
      if (has) fetchCoverage();
    })();
  }, [fetchPhenotype, fetchCoverage, refreshKey, localRefresh]);

  // Don't render if user has no phenotype with research record
  if (!phenotype?.has_research) return null;
  if (loading && !coverage) {
    return (
      <div className={`rounded-lg p-3 text-[11px] italic ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-50 text-gray-500"}`}
        data-testid="pathway-coverage-loading">
        {t.calculating}
      </div>
    );
  }
  if (!coverage?.supported) return null;

  const { coverage_count, total_interventions, coverage_pct, pathway_coverage_count, pathway_coverage_total, hits, misses, active_pathways, all_pathways, meals_logged, menopausal_status, weighted_pct, mindbody, combined_pathway_coverage_count, combined_pathway_coverage_pct } = coverage;

  const pct = coverage_pct || 0;
  const pctColor = pct >= 70 ? "#10B981" : pct >= 40 ? "#F59E0B" : "#EF4444";

  return (
    <div className={`rounded-lg overflow-hidden border ${isDark ? "border-purple-500/20 bg-purple-500/5" : "border-purple-200 bg-purple-50/40"}`}
      data-testid="pathway-coverage-card">
      {/* Header */}
      <button onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/5 transition-colors"
        data-testid="pathway-coverage-header">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isDark ? "bg-purple-500/20" : "bg-purple-100"}`}>
          <Target className={`w-4 h-4 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 flex-wrap ${isDark ? "text-purple-300" : "text-purple-700"}`}>
            <span>{t.pathwayCoverage}</span>
            {menopausal_status && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] normal-case tracking-normal font-semibold
                ${isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"}`}
                data-testid="meno-badge">
                <User className="w-2.5 h-2.5" />
                {menoLabel(menopausal_status)}
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {coverage_count}/{total_interventions} {t.interventions}
            </span>
            <span className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              · {pathway_coverage_count}/{pathway_coverage_total} {t.pathways}
            </span>
            {weighted_pct !== null && weighted_pct !== undefined && (
              <span className={`text-[10px] font-semibold ${isDark ? "text-pink-300" : "text-pink-600"}`}
                title={`Weighted score · ${menoLabel(menopausal_status).toLowerCase()}`}
                data-testid="weighted-pct-badge">
                · {weighted_pct}% {t.stratified}
              </span>
            )}
            {meals_logged !== undefined && (
              <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                · {meals_logged} {t.mealsToday}
              </span>
            )}
            {mindbody?.sessions_logged > 0 && (
              <span className={`text-[10px] font-semibold ${isDark ? "text-violet-300" : "text-violet-600"}`}
                title="Mind-Body pathway contribution"
                data-testid="mindbody-contribution-badge">
                · +{mindbody.extra_pathways_count} {t.pathways} · MBI ({mindbody.total_minutes} min)
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 relative w-10 h-10">
          <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke={isDark ? "#27272a" : "#e5e7eb"} strokeWidth="3" />
            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke={pctColor} strokeWidth="3"
              strokeDasharray={`${pct}, 100`} strokeLinecap="round" />
          </svg>
          <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold`}
            style={{ color: pctColor }}>
            {Math.round(pct)}%
          </span>
        </div>
      </button>

      {/* Body — expandable */}
      {expanded && (
        <div className={`px-3 pb-3 space-y-2.5 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
          {/* Menopausal toggle — показан за subtypes with weights */}
          {["LUMINAL_A", "LUMINAL_B_HER2_NEG", "LUMINAL_B_HER2_POS", "LUMINAL_B"].includes(phenotype?.code) && (
            <div className="pt-2.5">
              <MenoToggle API={API} current={menopausal_status}
                onChanged={() => setLocalRefresh(x => x + 1)} />
            </div>
          )}          {/* Hits */}
          {hits?.length > 0 && (
            <div className="pt-2.5">
              <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1.5 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                ✓ {t.coveredInterventions} ({hits.length})
              </div>
              <ul className="space-y-1">
                {hits.map((h) => {
                  const cfg = EVIDENCE_COLORS[h.evidence_level] || EVIDENCE_COLORS.C;
                  return (
                    <li key={h.intervention_id}
                      className={`flex items-start gap-2 text-[11px] rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}
                      data-testid={`coverage-hit-${h.intervention_id}`}>
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className={`font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {h.nutrient_bg.split(" (")[0]}
                          </span>
                          <span className={`text-[9px] px-1 rounded font-bold ${isDark ? cfg.bg + " " + cfg.text : cfg.light}`}>
                            {h.evidence_level}
                          </span>
                        </div>
                        <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          → {h.matched_food}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Misses */}
          {misses?.length > 0 && (
            <div>
              <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                ◯ {t.uncovered} ({misses.length}) — {t.uncoveredHint}
              </div>
              <ul className="space-y-1">
                {misses.map((m) => {
                  const cfg = EVIDENCE_COLORS[m.evidence_level] || EVIDENCE_COLORS.C;
                  const isOpen = openGap === m.intervention_id;
                  return (
                    <li key={m.intervention_id}
                      className={`rounded p-1.5 ${isDark ? "bg-white/5" : "bg-white"}`}
                      data-testid={`coverage-miss-${m.intervention_id}`}>
                      <div className="flex items-start gap-2 text-[11px]">
                        <Circle className="w-3 h-3 text-gray-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                              {m.nutrient_bg.split(" (")[0]}
                            </span>
                            <span className={`text-[9px] px-1 rounded font-bold ${isDark ? cfg.bg + " " + cfg.text : cfg.light}`}>
                              {m.evidence_level}
                            </span>
                          </div>
                        </div>
                        <button onClick={() => setOpenGap(isOpen ? null : m.intervention_id)}
                          className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors
                            ${isOpen
                              ? (isDark ? "bg-amber-500/30 text-amber-200" : "bg-amber-200 text-amber-900")
                              : (isDark ? "bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "bg-amber-100 text-amber-700 hover:bg-amber-200")}`}
                          data-testid={`gap-suggest-btn-${m.intervention_id}`}
                          title={t.suggestFood}>
                          <Lightbulb className="w-2.5 h-2.5" />
                          {isOpen ? t.hideGap : t.closeGap}
                        </button>
                      </div>
                      {isOpen && (
                        <GapSuggestPanel
                          API={API}
                          interventionId={m.intervention_id}
                          date={date}
                          onAdded={() => { setOpenGap(null); setLocalRefresh(x => x + 1); }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Active pathways */}
          {active_pathways?.length > 0 && (
            <div>
              <div className={`text-[9px] uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1 ${isDark ? "text-cyan-400" : "text-cyan-700"}`}>
                <TrendingUp className="w-3 h-3" />
                {t.activatedKegg} ({active_pathways.length}/{all_pathways?.length || 0})
              </div>
              <div className="flex flex-wrap gap-1">
                {all_pathways?.map((p) => {
                  const active = active_pathways.some((a) => a.kegg_id === p.kegg_id);
                  return (
                    <a key={p.kegg_id} href={`https://www.kegg.jp/entry/${p.kegg_id}`}
                      target="_blank" rel="noopener noreferrer"
                      className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-mono transition-all
                        ${active
                          ? (isDark ? "bg-cyan-500/20 text-cyan-300" : "bg-cyan-100 text-cyan-700")
                          : (isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400")}
                        ${active ? "" : "opacity-60"}`}
                      title={p.name_bg}
                      data-testid={`pathway-chip-${p.kegg_id}`}>
                      {active && "✓ "}{p.name_bg}
                      <ExternalLink className="w-2 h-2" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
